import React, { useContext, useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { List, ListFilterResponse, OrderDirection } from '../../Models/Models';
import { SiteContext } from '../../Contexts';
import { ListService } from '../../Services/ListService';
import { EntityColors } from '../../Themes/EntityColors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import CustomTooltip from '../Tooltips/CustomTooltip';
import EditButton from '../Buttons/EditButton';
import { Utils } from '../../Services/Utils';
import DeleteButton from '../Buttons/DeleteButton';
import { ListItemService } from '../../Services/ListItemService';
import ReusableModal from '../Modals/ReusableModal';
import TableShell from '../Table/TableShell';
import TableToolbar from '../Table/TableToolbar';

const ListTableComponent = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Zoznamy');
    }, []);

    const navigate = useNavigate();
    const { filterLists } = ListService();
    const { deleteListItem } = ListItemService();
    const [response, setResponse] = useState<ListFilterResponse | null>(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(6);
    const [orderDirection, setOrderDirection] = useState<OrderDirection>('Desc');
    const { currentSite }: any = useContext(SiteContext);
    const { action, icon } = EntityColors.list;

    const [showModal, setShowModal] = useState(false);
    const [toDeleteId, setToDeleteId] = useState<number | null>(null);

    const loadPage = async (pageNum: number, opts?: { perPage?: number; order?: OrderDirection }) => {
        const limit = opts?.perPage ?? perPage;
        const resp = await filterLists({
            websiteId: currentSite.id,
            limit,
            offset: (pageNum - 1) * limit,
            orderDirection: opts?.order ?? orderDirection,
        });
        setResponse(resp ?? null);
    };

    const changePerPage = (count: number) => {
        setPerPage(count);
        setPage(1);
        void loadPage(1, { perPage: count });
    };

    const changeOrder = (dir: OrderDirection) => {
        setOrderDirection(dir);
        setPage(1);
        void loadPage(1, { order: dir });
    };

    const confirmDelete = () => {
        if (toDeleteId !== null) {
            deleteListItem(toDeleteId).then(() => {
                setShowModal(false);
                setToDeleteId(null);
                loadPage(1);
                setPage(1);
            });
        }
    };

    useEffect(() => {
        setPage(1);
        void loadPage(1);
    }, [currentSite]);

    return (
        <>
            <TableShell
                title="Zoznamy"
                icon={icon}
                accentColor={action}
                isLoading={response === null}
                isEmpty={response !== null && response.data.length === 0}
                emptyMessage="Žiadne zoznamy"
                page={page}
                totalCount={response?.totalCount}
                perPage={perPage}
                onPageChange={p => { setPage(p); loadPage(p); }}
                toolbar={
                    <TableToolbar
                        totalCount={response?.totalCount ?? 0}
                        nounForms={['zoznam', 'zoznamy', 'zoznamov']}
                        perPage={perPage}
                        onPerPageChange={changePerPage}
                        orderDirection={orderDirection}
                        onOrderChange={changeOrder}
                    />
                }
            >
                <Table responsive>
                    <thead>
                        <tr>
                            <th>Kód</th>
                            <th>Text</th>
                            <th>Odkaz</th>
                            <th className="w-1 text-center">Akcie</th>
                        </tr>
                    </thead>
                    <tbody>
                        {response?.data.map((list: List) => {
                            const items = Array.isArray(list.listItemData) ? list.listItemData : [];
                            const totalRows = Math.max(1, items.length) + 1;

                            return (
                                <React.Fragment key={list.code}>
                                    {items.length > 0 ? (
                                        items.map((item: any, index: number) => (
                                            <tr key={`${list.code}-${item.id}`} className={index === 0 ? 'table-group-separator' : ''}>
                                                {index === 0 && (
                                                    <td className="align-middle" rowSpan={totalRows} style={{ fontWeight: 600, color: '#374151' }}>
                                                        {list.code}
                                                    </td>
                                                )}
                                                <td className="align-middle">{item.text}</td>
                                                <td className="align-middle">
                                                    {item.link ? (
                                                        <a href={item.link} target="_blank" rel="noreferrer" style={{ color: action }}>
                                                            {item.link}
                                                        </a>
                                                    ) : (
                                                        <span style={{ color: '#94A3B8' }}>—</span>
                                                    )}
                                                </td>
                                                <td className="align-middle text-center">
                                                    <div style={{ display: 'inline-flex', gap: '0.4rem' }}>
                                                        <CustomTooltip placement="left" message="Upraviť">
                                                            <Link to={`/listitem/edit/${item.id}`}>
                                                                <EditButton color={action} iconOnly />
                                                            </Link>
                                                        </CustomTooltip>
                                                        <CustomTooltip placement="right" message="Zmazať">
                                                            <DeleteButton
                                                                color={action}
                                                                iconOnly
                                                                onClick={() => { setToDeleteId(item.id); setShowModal(true); }}
                                                            />
                                                        </CustomTooltip>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr key={`${list.code}-empty`} className="table-group-separator">
                                            <td rowSpan={totalRows} style={{ fontWeight: 600, color: '#374151', verticalAlign: 'middle' }}>
                                                {list.code.replace('_', ' ')}
                                            </td>
                                        </tr>
                                    )}

                                    <tr key={`${list.code}-add`} className={items.length === 0 ? 'table-group-separator' : ''}>
                                        <td colSpan={3} className="text-center" style={{ padding: '0.6rem 1rem' }}>
                                            <button
                                                onClick={() => navigate(`/listItem/create/${list.id}`)}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.35rem',
                                                    padding: '0.3rem 0.75rem',
                                                    borderRadius: '7px',
                                                    border: `1.5px solid ${action}`,
                                                    backgroundColor: 'transparent',
                                                    color: action,
                                                    fontWeight: 600,
                                                    fontSize: '0.78rem',
                                                    cursor: 'pointer',
                                                    transition: 'background-color 0.15s',
                                                }}
                                                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#ECFDF5')}
                                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.7rem' }} />
                                                Pridať položku
                                            </button>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </Table>
            </TableShell>

            <ReusableModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSubmit={confirmDelete}
                title="Potvrdiť zmazanie"
                description="Naozaj chcete zmazať túto položku?"
            />
        </>
    );
};

export default ListTableComponent;
